// src/pages/live-scorer/EventLogForm.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import type { Match, EventType, Player } from "@/types/api.types";
import { usePlayersAdmin } from "@/hooks/api/usePlayers";

interface EventLogFormProps {
  match: Match;
  onSubmit: (data: {
    playerId: number;
    eventType: EventType;
    minute: number;
    assistPlayerId?: number;
  }) => void;
  onCancel: () => void;
  isLogging: boolean;
  defaultMinute?: number;
}

const EVENT_BUTTONS: {
  value: EventType;
  label: string;
  active: string;
  inactive: string;
}[] = [
  {
    value: "GOAL",
    label: "⚽ Goal",
    active: "bg-green-600 text-white border-green-600",
    inactive: "bg-green-50 text-green-800 border-green-300 hover:bg-green-100",
  },
  {
    value: "ASSIST",
    label: "🅰️ Assist",
    active: "bg-blue-600 text-white border-blue-600",
    inactive: "bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100",
  },
  {
    value: "YELLOW_CARD",
    label: "🟨 Yellow",
    active: "bg-yellow-500 text-white border-yellow-500",
    inactive:
      "bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100",
  },
  {
    value: "RED_CARD",
    label: "🟥 Red Card",
    active: "bg-red-600 text-white border-red-600",
    inactive: "bg-red-50 text-red-800 border-red-300 hover:bg-red-100",
  },
  {
    value: "PENALTY_SAVED",
    label: "🧱 Pen Saved",
    active: "bg-purple-600 text-white border-purple-600",
    inactive:
      "bg-purple-50 text-purple-800 border-purple-300 hover:bg-purple-100",
  },
  {
    value: "PENALTY_MISSED",
    label: "❌ Pen Miss",
    active: "bg-orange-600 text-white border-orange-600",
    inactive:
      "bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100",
  },
  {
    value: "OWN_GOAL",
    label: "😬 Own Goal",
    active: "bg-gray-700 text-white border-gray-700",
    inactive: "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200",
  },
  {
    value: "CLEAN_SHEET",
    label: "🧤 Clean Sheet",
    active: "bg-teal-600 text-white border-teal-600",
    inactive: "bg-teal-50 text-teal-800 border-teal-300 hover:bg-teal-100",
  },
];

const POSITION_ORDER = ["GK", "DEF", "MID", "FWD"];

const POSITION_COLORS: Record<string, string> = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-red-100 text-red-800",
};

function PlayerButton({
  player,
  isSelected,
  onSelect,
  selectedStyle,
}: {
  player: Player;
  isSelected: boolean;
  onSelect: (p: Player) => void;
  selectedStyle: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(player)}
      className={`w-full text-left px-2 py-1.5 rounded-lg border text-xs transition-all flex items-center gap-1.5 ${
        isSelected
          ? `${selectedStyle} ring-2 ring-offset-1`
          : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <Badge
        className={`text-xs shrink-0 px-1 w-8 justify-center ${POSITION_COLORS[player.position]}`}
      >
        {player.position}
      </Badge>
      <span className={`truncate ${isSelected ? "font-bold" : "font-medium"}`}>
        {player.name}
      </span>
      {player.jerseyNumber != null && (
        <span
          className={`shrink-0 text-xs ${isSelected ? "opacity-80" : "text-gray-400"}`}
        >
          #{player.jerseyNumber}
        </span>
      )}
    </button>
  );
}

export default function EventLogForm({
  match,
  onSubmit,
  onCancel,
  isLogging,
  defaultMinute,
}: EventLogFormProps) {
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [minute, setMinute] = useState(defaultMinute ?? 1);
  const [addAssist, setAddAssist] = useState(false);
  const [assistPlayer, setAssistPlayer] = useState<Player | null>(null);

  // Fetch both teams upfront
  const { data: homeData, isLoading: homeLoading } = usePlayersAdmin({
    nationId: match.homeNationId,
    isActive: true,
    limit: 30,
  });
  const { data: awayData, isLoading: awayLoading } = usePlayersAdmin({
    nationId: match.awayNationId,
    isActive: true,
    limit: 30,
  });

  const sortByPosition = (a: Player, b: Player) =>
    POSITION_ORDER.indexOf(a.position) - POSITION_ORDER.indexOf(b.position);

  const homePlayers = [...(homeData?.players ?? [])].sort(sortByPosition);
  const awayPlayers = [...(awayData?.players ?? [])].sort(sortByPosition);

  // Determine scorer's team for assist filter
  const isHomeScorerPlayer = homePlayers.some(
    (p) => p.id === selectedPlayer?.id
  );
  const sameTeamPlayers = isHomeScorerPlayer ? homePlayers : awayPlayers;

  const handleEventSelect = (type: EventType) => {
    setSelectedEvent(type);
    // Assist only makes sense for GOAL; clear otherwise
    if (type !== "GOAL") {
      setAddAssist(false);
      setAssistPlayer(null);
    }
  };

  const handleSubmit = () => {
    if (!selectedPlayer || !selectedEvent) return;
    onSubmit({
      playerId: selectedPlayer.id,
      eventType: selectedEvent,
      minute,
      assistPlayerId:
        addAssist && assistPlayer ? assistPlayer.id : undefined,
    });
  };

  const canSubmit = !!selectedPlayer && !!selectedEvent && !isLogging;

  const selectedEventLabel =
    EVENT_BUTTONS.find((e) => e.value === selectedEvent)?.label ?? "";

  if (homeLoading || awayLoading) {
    return (
      <div className="flex items-center justify-center py-6 gap-2 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading players…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-800 text-sm">Log New Event</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-gray-500 text-xs h-7"
        >
          Cancel
        </Button>
      </div>

      {/* ── Step 1: Player Grid ── */}
      <div className="grid grid-cols-2 gap-3">
        {/* Home Team */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
            {match.homeNation.flagUrl && (
              <img
                src={match.homeNation.flagUrl}
                className="w-5 h-3.5 object-cover rounded"
                alt=""
              />
            )}
            <span className="text-xs font-bold text-gray-700">
              {match.homeNation.name}
            </span>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto pr-0.5">
            {homePlayers.map((p) => (
              <PlayerButton
                key={p.id}
                player={p}
                isSelected={selectedPlayer?.id === p.id}
                onSelect={(pl) => {
                  setSelectedPlayer(pl);
                  setAssistPlayer(null);
                }}
                selectedStyle="bg-blue-100 border-blue-500 text-blue-900"
              />
            ))}
          </div>
        </div>

        {/* Away Team */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
            {match.awayNation.flagUrl && (
              <img
                src={match.awayNation.flagUrl}
                className="w-5 h-3.5 object-cover rounded"
                alt=""
              />
            )}
            <span className="text-xs font-bold text-gray-700">
              {match.awayNation.name}
            </span>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto pr-0.5">
            {awayPlayers.map((p) => (
              <PlayerButton
                key={p.id}
                player={p}
                isSelected={selectedPlayer?.id === p.id}
                onSelect={(pl) => {
                  setSelectedPlayer(pl);
                  setAssistPlayer(null);
                }}
                selectedStyle="bg-purple-100 border-purple-500 text-purple-900"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Selected player chip */}
      {selectedPlayer && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs">
          <span className="text-gray-400">Player:</span>
          <Badge
            className={`text-xs ${POSITION_COLORS[selectedPlayer.position]}`}
          >
            {selectedPlayer.position}
          </Badge>
          <span className="font-semibold text-gray-900">
            {selectedPlayer.name}
          </span>
          {selectedPlayer.jerseyNumber != null && (
            <span className="text-gray-400">
              #{selectedPlayer.jerseyNumber}
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              setSelectedPlayer(null);
              setSelectedEvent(null);
              setAssistPlayer(null);
              setAddAssist(false);
            }}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Step 2: Event Type Grid ── */}
      <div>
        <Label className="text-xs text-gray-600 mb-1.5 block">
          Event Type
        </Label>
        <div className="grid grid-cols-4 gap-1.5">
          {EVENT_BUTTONS.map((btn) => (
            <button
              key={btn.value}
              type="button"
              onClick={() => handleEventSelect(btn.value)}
              className={`px-2 py-2 rounded-lg border text-xs font-medium transition-colors ${
                selectedEvent === btn.value ? btn.active : btn.inactive
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Assist section — GOAL only ── */}
      {selectedEvent === "GOAL" && (
        <div className="border border-blue-200 rounded-lg p-3 bg-blue-50 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={addAssist}
              onChange={(e) => {
                setAddAssist(e.target.checked);
                if (!e.target.checked) setAssistPlayer(null);
              }}
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
            />
            <span className="text-xs font-semibold text-blue-700">
              🅰️ Has Assist?
            </span>
          </label>

          {addAssist && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {sameTeamPlayers
                .filter((p) => p.id !== selectedPlayer?.id)
                .map((p) => (
                  <PlayerButton
                    key={p.id}
                    player={p}
                    isSelected={assistPlayer?.id === p.id}
                    onSelect={setAssistPlayer}
                    selectedStyle="bg-blue-200 border-blue-500 text-blue-900"
                  />
                ))}
            </div>
          )}

          {addAssist && assistPlayer && (
            <p className="text-xs text-blue-700 font-medium">
              Assist: {assistPlayer.name}
            </p>
          )}
        </div>
      )}

      {/* ── Step 3: Minute + Submit ── */}
      <div className="flex items-end gap-3 pt-1 border-t">
        <div>
          <Label
            htmlFor="event-minute"
            className="text-xs text-gray-600 mb-1 block"
          >
            Minute
          </Label>
          <Input
            id="event-minute"
            type="number"
            min={1}
            max={120}
            value={minute}
            onChange={(e) =>
              setMinute(Math.max(1, Math.min(120, Number(e.target.value))))
            }
            className="w-20 bg-white border-gray-300 text-center font-bold"
          />
        </div>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
        >
          {isLogging ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Logging…
            </>
          ) : canSubmit ? (
            `Log ${selectedEventLabel} for ${selectedPlayer!.name}`
          ) : (
            "Select player & event type"
          )}
        </Button>
      </div>
    </div>
  );
}
